import { HttpContext } from '@angular/common/http';
import { IGNORE_SPACE_CONTEXT, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { Component, computed, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { FilesListStateService } from '@modules/files/services/files-list-state.service';
import { FilesService } from '@modules/files/services/files.service';
import { FilesTenantScopeService } from '@modules/files/services/files-tenant-scope.service';
import { FoldersListStateService } from '@modules/files/services/folders-list-state.service';
import { FilesAppStateService } from '@modules/files/services/state.service';
import { File } from '@modules/files/types/file';
import { hasFileLink } from '@modules/files/types/file-link';
import { FilesFilters } from '@modules/files/types/files-filters';
import { Folder } from '@modules/files/types/folder';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { type ListState } from '@modules/files/types/list-state';
import { VirtualFolder } from '@modules/files/types/virtual-folder';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-files-panel',
  templateUrl: './files-panel.component.html',
  styleUrls: ['./files-panel.component.less'],
  standalone: false,
  providers: [
    ...FilesListStateService.providers({
      stateKey: StateKey.filesDPListState,
      withAdvancedSearch: true,
    }),
    ...FoldersListStateService.providers({
      stateKey: StateKey.foldersDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class FilesPanelComponent implements OnInit, OnDestroy {
  public foldersFilters: FoldersFilters;
  public filesFilters: FilesFilters;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  public tabs: Tab[] = [
    { title: 'Folders', value: 'folders' },
    { title: 'Files', value: 'files' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'files';

  protected readonly popoverClose = new Subject<void>();
  private alive = new Subject<void>();

  private readonly filesListStateService = inject(FilesListStateService, { self: true });
  private readonly foldersListStateService = inject(FoldersListStateService, { self: true });

  private readonly filesService = inject(FilesService);
  private readonly dpService = inject(DynamicPanelService);
  private readonly modalService = inject(ModalService);
  private readonly injector = inject(Injector);
  private readonly searchService = inject(AdvancedSearchService);
  private readonly stateService = inject(FilesAppStateService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly spaceContextStore = inject(SpaceContextStore);
  /** The panel's own Files section (its 'dp' copy): no rail resolves it, so this panel does (#2068). */
  private readonly filesTenantScope = inject(FilesTenantScopeService, { optional: true });
  /** Provided by the side panel host for its own workspace menu; names the caller's member tenant in the team. */
  private readonly workspaceIdentity = inject(WorkspaceIdentityService, { optional: true });

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on file rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.searchService.isAdvancedSearchApplied(Application.files),
    { initialValue: false },
  );
  /**
   * Search-shaped: the shared query/markers plus transient ids. Files deliberately reports NO form-only
   * filter — its `fileType` round-trips from the list's filter menu, so it lights the funnel pill and survives
   * this pill's clear; the date range and `stitched` are excluded for the same reason.
   */
  protected readonly isSearchApplied: Signal<boolean> = toSignal(
    this.searchService.isSearchApplied(Application.files),
    { initialValue: false },
  );

  /**
   * Derived pages (Flagged/Follow Up/Snoozed/Archive/Trash) offer no creation form — DP-001. Derived
   * from the stored page rather than from `selectFilter`, so it cannot drift from the list's filters.
   */
  protected readonly creationAllowed: Signal<boolean> = toSignal(
    this.stateService.getVirtualFolder().pipe(map((folder) => isCreationAllowedInVirtualFolder(folder))),
    { initialValue: true },
  );
  private readonly filesTenantId$ = toObservable(this.dpService.filesTenantId).pipe(
    startWith(this.dpService.filesTenantId()),
    distinctUntilChanged(),
  );
  private readonly closedTenantItems = this.closedFileTenantItems();
  private readonly panelTenantItems = computed<ReadonlyArray<Pick<Stitch, 'accountId'>> | undefined>(() => {
    const tenantId = this.dpService.filesTenantId();
    return tenantId === undefined ? this.closedTenantItems : tenantId ? [{ accountId: tenantId }] : undefined;
  });

  /** Explicit personal composes omit the space stamp even when Files has a shared workspace selected. */
  protected readonly requestContext = computed(() => {
    const tenantId = this.dpService.filesTenantId();
    if (tenantId === undefined) return undefined;
    return tenantId === null
      ? new HttpContext().set(IGNORE_SPACE_CONTEXT, true)
      : new HttpContext().set(OWNING_SPACE_ID, tenantId);
  });

  protected get tenantItems(): ReadonlyArray<Pick<Stitch, 'accountId'>> | undefined {
    return this.panelTenantItems();
  }

  /**
   * Lifecycle
   */

  ngOnInit() {
    this.startFilesSection();

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([
      this.foldersListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.filesTenantId$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.foldersFilters = new FoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.filesListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.filesTenantId$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // short: true - load FileSummaryDto for the list
        this.filesFilters = new FilesFilters({ short: true, displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleNewFile(file = new File()) {
    this.modalService.openFullForm(file, this.injector);
  }

  openFileInModal(file: File) {
    this.filesService
      .getItem(file.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullFile) => {
        this.modalService.openFullForm(fullFile, this.injector);
      });
  }

  openFile(file: File) {
    if (hasFileLink(file)) {
      this.filesService.openLink(file);
      return;
    }
    this.filesService
      .getItem(file.id)
      .pipe(take(1), takeUntil(this.alive))
      .subscribe((fullFile) => {
        this.dpService.setFormItem(fullFile);
      });
  }

  openFolder(folder: Folder) {
    this.dpService.setFormItem(folder);
  }

  selectFilter(folder: VirtualFolder): void {
    this.selectedFilter = folder;
    this.stateService.setVirtualFolder(folder);
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }

  /**
   * A Files panel revealed by closing an opened file or folder keeps that item's owning tenant for its list:
   * opening it cleared the panel's Files tenant, so without this the back arrow lists with no owner (#1989).
   */
  private closedFileTenantItems(): ReadonlyArray<Pick<Stitch, 'accountId'>> | undefined {
    const item = this.dpService.takeLastClosedFormState()?.item;
    return item?.itemType === StitchType.FILE || item?.itemType === StitchType.FOLDER ? [item] : undefined;
  }

  /**
   * Inside a team an untouched panel Files section lists the TEAM (prototype app.js :34; the Notes panel's #2016 seed), stored
   * before the lists subscribe. Then the panel releases its own section ONCE with the caller's member tenant (or none when the
   * lookup fails or names none), so a stored legacy null resolves too: otherwise the panel's tabs, filters and lists waited out
   * the 10 s identity timeout, which only main's Files rail ever ends (#2068). Main's Files section is a separate copy.
   */
  private startFilesSection(): void {
    const scope = this.filesTenantScope;
    if (!scope || this.spaceContextStore.type() !== SpaceType.SHARED_SPACE) return;
    const team = this.spaceContextStore.contextId();
    if (team && scope.tenantId() === undefined) {
      scope.selectTenant(team);
    }
    if (scope.isTenantIdentityResolved()) return;
    (this.workspaceIdentity?.current() ?? of(undefined))
      .pipe(
        take(1),
        catchError(() => of(undefined)),
        takeUntil(this.alive),
      )
      .subscribe((identity) => scope.resolveMemberIdentity(identity?.memberTenantId ?? null));
  }

  /** "Add details" on the quick form: continue in the full form with what was typed. */
  protected handleNewFolder(folder: Stitch): void {
    this.modalService.openFullForm(folder, this.injector);
  }
}
